/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams, useRouter } from 'js/lib/useRouter';

import { Grid, LoadingSection, PageGridContainer, breakpoints } from '@coursera/cds-core';

import { NotFound } from 'bundles/common/components/withNotFound';
import ReviewsBackButton from 'bundles/reviews/components/ReviewsBackButton';
import ReviewsPageSeoElements from 'bundles/reviews/components/ReviewsPageSeoElements';
import ReviewsProductCard from 'bundles/reviews/components/ReviewsProductCard';
import ReviewsRatingCard from 'bundles/reviews/components/ReviewsRatingCard';
import ReviewsTable from 'bundles/reviews/components/ReviewsTable';
import TopReviewsSection from 'bundles/reviews/components/TopReviewsSection';
import { useReviewsPageData } from 'bundles/reviews/hooks/useReviewsPageData';
import type { ReviewRouter } from 'bundles/reviews/types/reviewTypes';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useIsInUnifiedPageRollout } from 'bundles/unified-description-page-common/providers/UnifiedPageRolloutExperimentDataProvider';

const NUM_TOP_REVIEWS_TO_SHOW = 12;

const styles = {
  layout: css`
    display: grid;
    grid-template-columns: minmax(240px, 300px) minmax(0, 1fr);
    grid-template-areas:
      'rating product'
      'rating content';
    gap: 42px;
    align-items: start;
    margin-top: var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      grid-template-columns: 1fr;
      grid-template-areas:
        'product'
        'rating'
        'content';
      gap: var(--cds-spacing-200);
    }
  `,
  ratingColumn: css`
    grid-area: rating;
    min-width: 0;
  `,
  productColumn: css`
    grid-area: product;
    min-width: 0;
  `,
  content: css`
    grid-area: content;
    min-width: 0;
  `,
};

const ReviewsPageDirector = () => {
  const router = useRouter();
  const params = useParams();
  const slug = params?.slug ?? '';
  const programSlug = params?.programSlug;
  const orgSlug = params?.orgSlug;
  const isInUnifiedPageRollout = useIsInUnifiedPageRollout();

  const { loading, error, notFound, courseInfo, topReviews = [], schemaReviews, headerData } = useReviewsPageData();

  if (loading) {
    return <LoadingSection />;
  }

  if (!slug || notFound || error || !courseInfo?.name) {
    if (inServerContext) {
      throw new NotFoundError();
    }
    return <NotFound />;
  }

  const courseId = courseInfo.id.split('~')[1];

  let coursePageHref = `/learn/${courseInfo.slug}`;
  if (isInUnifiedPageRollout && orgSlug) {
    coursePageHref = `/organizations/${orgSlug}/learn/${courseInfo.slug}`;
  } else if (programSlug) {
    coursePageHref = `/programs/${programSlug}/learn/${courseInfo.slug}`;
  }

  const pageProps = {
    slug,
    courseInfo,
    schemaReviews: schemaReviews ?? [],
    router: router as ReviewRouter,
  };
  const backButtonProps = {
    courseName: courseInfo.name,
    href: coursePageHref,
  };
  const topReviewsSectionProps = {
    topReviews: topReviews.slice(0, NUM_TOP_REVIEWS_TO_SHOW),
  };
  const reviewsTableProps = {
    courseId: courseInfo.id,
  };
  return (
    <PageGridContainer>
      <Grid item xs={12}>
        <ReviewsBackButton {...backButtonProps} />
        <div css={styles.layout}>
          <div css={styles.productColumn}>
            <ReviewsProductCard
              {...headerData}
              programSlug={programSlug}
              courseId={courseId ?? ''}
              courseName={headerData.courseName || courseInfo.name}
            />
          </div>
          <aside css={styles.ratingColumn} data-testid="reviews-rating-column">
            <ReviewsRatingCard
              averageFiveStarRating={headerData.averageFiveStarRating}
              ratingCount={headerData.ratingCount}
              ratingCountsByStar={headerData.ratingCountsByStar}
            />
          </aside>
          <div css={styles.content}>
            <TopReviewsSection {...topReviewsSectionProps} />
            <ReviewsTable {...reviewsTableProps} />
          </div>
        </div>
        <ReviewsPageSeoElements {...pageProps} />
      </Grid>
    </PageGridContainer>
  );
};

export default ReviewsPageDirector;
